/**
 * Operational access block helpers for auth / refreshToken / restricted billing.
 *
 * Credentials valid + company not full-access = HTTP 403 with authResult.
 * That is NOT an invalid password and must NOT open the main Automy shell.
 */

import { STORAGE_KEYS } from './sessionConstants';

export const OPERATIONAL_REFRESH_BLOCK_CODES = [
  'COMPANY_INACTIVE',
  'SUBSCRIPTION_NOT_FOUND',
  'SUBSCRIPTION_PENDING_ACTIVATION',
  'SUBSCRIPTION_PENDING_PAYMENT',
  'PAYMENT_METHOD_REQUIRED',
  'SUBSCRIPTION_PAST_DUE',
  'SUBSCRIPTION_CANCELED',
  'SUBSCRIPTION_SUSPENDED',
];

export const OPERATIONAL_BLOCK_ROUTE = '/user/login/access-blocked';
export const BILLING_PAYMENT_SETUP_ROUTE = '/billing/payment-setup';
export const BILLING_PAYMENT_ROUTE = '/billing/payment';
export const SUPPORT_ROUTE = '/support';
export const SUPPORT_MAILTO = 'mailto:support@automy.global';

const BILLING_ACTIONS = ['setup_payment_method', 'pay_subscription'];
const BILLING_RECOVERY_PREFIXES = [BILLING_PAYMENT_SETUP_ROUTE, BILLING_PAYMENT_ROUTE];

/**
 * @param {string|null|undefined} path
 * @returns {boolean}
 */
export function isRestrictedBillingRecoveryPath(path) {
  if (!path || typeof path !== 'string') return false;
  return BILLING_RECOVERY_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/**
 * Volatile restricted-billing session (memory only — never localStorage/sessionStorage).
 * @type {{ token: string, expiresAt: number|null, authResult: object, code?: string, message?: string, status?: number }|null}
 */
let restrictedBillingSession = null;

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isOperationalAccessBlockedResponse(value) {
  if (!value || typeof value !== 'object') return false;

  const response = value;
  return Boolean(
    response.authResult &&
      response.authResult.credentialsValid === true &&
      typeof response.code === 'string',
  );
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isOperationalRefreshBlockedResponse(value) {
  if (!isOperationalAccessBlockedResponse(value)) return false;
  return OPERATIONAL_REFRESH_BLOCK_CODES.includes(value.code);
}

/**
 * @param {object} response
 * @returns {boolean}
 */
export function hasRestrictedBillingToken(response) {
  const authResult = response?.authResult;
  const canAccess =
    authResult?.canAccessBillingSetup === true || response?.canAccessBillingSetup === true;
  const accessMode = authResult?.accessMode || response?.accessMode;

  return Boolean(response?.restrictedToken && accessMode === 'restricted-billing' && canAccess);
}

/**
 * Apply GET /billing/restricted/access-state to volatile session.
 * Replaces restrictedToken when backend issues a new one (setup → pay).
 *
 * @param {object} accessState - RestrictedBillingAccessStateResponse
 * @returns {{
 *   kind: 'awaiting_payment_method'|'ready_for_payment'|'login_required'|'blocked'|'unknown',
 *   route: string|null,
 *   code?: string,
 *   accessState: object,
 * }}
 */
export function applyRestrictedAccessStateResponse(accessState) {
  if (!accessState || typeof accessState !== 'object') {
    return { kind: 'unknown', route: null, accessState };
  }

  const code = accessState.code;
  const authResult = accessState.authResult || {};
  const actions = Array.isArray(authResult.allowedActions)
    ? authResult.allowedActions
    : Array.isArray(accessState.allowedActions)
    ? accessState.allowedActions
    : [];

  const requiresLogin =
    accessState.requiresLogin === true ||
    code === 'ACCESS_ALLOWED_LOGIN_REQUIRED' ||
    (authResult.accessAllowed === true && authResult.accessMode === 'full');

  if (requiresLogin) {
    clearRestrictedBillingSession();
    clearOperationalAuthBlock();
    return { kind: 'login_required', route: '/user/login', code, accessState };
  }

  // Webhook lag after setup: still PAYMENT_METHOD_REQUIRED — keep polling, no login.
  const stillNeedsSetup =
    code === 'PAYMENT_METHOD_REQUIRED' ||
    (actions.includes('setup_payment_method') && !actions.includes('pay_subscription'));

  if (stillNeedsSetup) {
    if (hasRestrictedBillingToken(accessState)) {
      setRestrictedBillingSession(accessState);
      setOperationalAuthBlock(accessState);
    } else {
      setOperationalAuthBlock(accessState);
    }
    return {
      kind: 'awaiting_payment_method',
      route: BILLING_PAYMENT_SETUP_ROUTE,
      code,
      accessState,
    };
  }

  if (hasRestrictedBillingToken(accessState)) {
    setRestrictedBillingSession(accessState);
    setOperationalAuthBlock(accessState);
    persistRestrictedBillingCheckoutHandoff();

    if (actions.includes('pay_subscription') || code === 'SUBSCRIPTION_PENDING_PAYMENT') {
      const route =
        (authResult.redirectTo && isRestrictedBillingRecoveryPath(authResult.redirectTo)
          ? authResult.redirectTo
          : null) ||
        (accessState.redirectTo && isRestrictedBillingRecoveryPath(accessState.redirectTo)
          ? accessState.redirectTo
          : null) ||
        BILLING_PAYMENT_ROUTE;
      return { kind: 'ready_for_payment', route, code, accessState };
    }

    const route = resolveOperationalBlockRoute(accessState, { hasRestrictedToken: true });
    return { kind: 'ready_for_payment', route, code, accessState };
  }

  clearRestrictedBillingSession();
  setOperationalAuthBlock(accessState);
  const route = resolveOperationalBlockRoute(accessState, { hasRestrictedToken: false });
  return { kind: 'blocked', route, code, accessState };
}

/**
 * Resolve where to send the user after an operational block (login or refresh).
 * Refresh never emits restrictedToken — billing actions then fall back to access-blocked.
 *
 * @param {object} response - Body with authResult
 * @param {{ hasRestrictedToken?: boolean }} [options]
 * @returns {string}
 */
export function resolveOperationalBlockRoute(response, options = {}) {
  const authResult = response?.authResult || response;
  const actions = Array.isArray(authResult?.allowedActions) ? authResult.allowedActions : [];
  const withToken =
    options.hasRestrictedToken === true ||
    hasRestrictedBillingToken(response) ||
    !!getRestrictedBillingToken();

  const redirectTo = authResult?.redirectTo;
  if (redirectTo && typeof redirectTo === 'string') {
    // Never open restricted-billing recovery without a volatile restrictedToken.
    if (!isRestrictedBillingRecoveryPath(redirectTo) || withToken) {
      return redirectTo;
    }
  }

  if (withToken && actions.includes('setup_payment_method')) {
    return BILLING_PAYMENT_SETUP_ROUTE;
  }
  if (withToken && actions.includes('pay_subscription')) {
    return BILLING_PAYMENT_ROUTE;
  }

  if (actions.includes('contact_support')) {
    return SUPPORT_ROUTE;
  }

  return OPERATIONAL_BLOCK_ROUTE;
}

/**
 * @param {object|null|undefined} authResult
 * @returns {boolean}
 */
export function requiresReloginForBillingAction(authResult) {
  const actions = authResult?.allowedActions;
  if (!Array.isArray(actions) || !actions.length) return false;
  if (getRestrictedBillingToken() || authResult?.restrictedToken) return false;
  return actions.some((action) => BILLING_ACTIONS.includes(action));
}

/**
 * Persist operational block payload for the public block screen.
 * Never persists restrictedToken (volatile memory only).
 *
 * @param {object} payload - Full response or { authResult, code, message, ... }
 */
export function setOperationalAuthBlock(payload) {
  try {
    const value = {
      authResult: payload?.authResult || payload,
      code: payload?.code,
      message: payload?.message,
      status: payload?.status,
      savedAt: Date.now(),
    };
    // Strip any accidental token from nested payload
    if (value.authResult && value.authResult.restrictedToken) {
      const { restrictedToken, ...rest } = value.authResult;
      value.authResult = rest;
    }
    sessionStorage.setItem(STORAGE_KEYS.OPERATIONAL_AUTH_BLOCK, JSON.stringify(value));
  } catch (e) {}
}

/**
 * @returns {object|null}
 */
export function getOperationalAuthBlock() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.OPERATIONAL_AUTH_BLOCK);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

export function clearOperationalAuthBlock() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.OPERATIONAL_AUTH_BLOCK);
  } catch (e) {}
}

/**
 * Store restricted billing token in memory only.
 * @param {object} response - AuthOperationalAccessBlockedResponse
 */
export function setRestrictedBillingSession(response) {
  if (!response?.restrictedToken) {
    restrictedBillingSession = null;
    return;
  }

  const expiresIn = Number(response.restrictedTokenExpiresIn) || 600;
  restrictedBillingSession = {
    token: response.restrictedToken,
    expiresAt: Date.now() + expiresIn * 1000,
    authResult: response.authResult,
    code: response.code,
    message: response.message,
    status: response.status,
  };
}

/**
 * @returns {string|null}
 */
export function getRestrictedBillingToken() {
  if (!restrictedBillingSession?.token) return null;
  if (restrictedBillingSession.expiresAt && Date.now() >= restrictedBillingSession.expiresAt) {
    restrictedBillingSession = null;
    return null;
  }
  return restrictedBillingSession.token;
}

/**
 * @returns {object|null}
 */
export function getRestrictedBillingSession() {
  if (!getRestrictedBillingToken()) return null;
  return restrictedBillingSession;
}

export function clearRestrictedBillingSession() {
  restrictedBillingSession = null;
  clearRestrictedBillingCheckoutHandoff();
}

/**
 * Persist restricted session for Stripe Checkout round-trip only.
 * Full navigation to Stripe wipes in-memory state; restore on success/cancel return.
 * Cleared after polling finishes or user leaves recovery.
 *
 * Note: this is a short-lived sessionStorage handoff for the Stripe redirect only —
 * not a general restricted-token store. Cleared as soon as checkout-status is final
 * or the user exits recovery.
 */
export function persistRestrictedBillingCheckoutHandoff() {
  const session = getRestrictedBillingSession();
  if (!session?.token) return;

  try {
    sessionStorage.setItem(
      STORAGE_KEYS.RESTRICTED_BILLING_CHECKOUT_HANDOFF,
      JSON.stringify({
        token: session.token,
        expiresAt: session.expiresAt,
        authResult: session.authResult,
        code: session.code,
        message: session.message,
        status: session.status,
      }),
    );
  } catch (e) {}
}

/**
 * Restore in-memory restricted session from Stripe handoff if still valid.
 * @returns {boolean}
 */
export function restoreRestrictedBillingCheckoutHandoff() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.RESTRICTED_BILLING_CHECKOUT_HANDOFF);
    if (!raw) return !!getRestrictedBillingToken();

    const parsed = JSON.parse(raw);
    if (!parsed?.token) return false;
    if (parsed.expiresAt && Date.now() >= parsed.expiresAt) {
      clearRestrictedBillingCheckoutHandoff();
      return false;
    }

    restrictedBillingSession = {
      token: parsed.token,
      expiresAt: parsed.expiresAt || Date.now() + 600 * 1000,
      authResult: parsed.authResult,
      code: parsed.code,
      message: parsed.message,
      status: parsed.status,
    };
    return true;
  } catch (e) {
    return false;
  }
}

export function clearRestrictedBillingCheckoutHandoff() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.RESTRICTED_BILLING_CHECKOUT_HANDOFF);
  } catch (e) {}
}

/**
 * Prefer backend message; fall back to code-based i18n id.
 *
 * @param {object|null} block
 * @returns {{ messageId: string, message?: string, code?: string }}
 */
export function getOperationalBlockCopy(block) {
  const code = block?.code || block?.authResult?.blockReason;
  const message = block?.message;

  const codeToMessageId = {
    COMPANY_INACTIVE: 'user.accessBlocked.code.companyInactive',
    SUBSCRIPTION_NOT_FOUND: 'user.accessBlocked.code.subscriptionNotFound',
    SUBSCRIPTION_PENDING_ACTIVATION: 'user.accessBlocked.code.pendingActivation',
    SUBSCRIPTION_PENDING_PAYMENT: 'user.accessBlocked.code.pendingPayment',
    PAYMENT_METHOD_REQUIRED: 'user.accessBlocked.code.paymentMethodRequired',
    SUBSCRIPTION_PAST_DUE: 'user.accessBlocked.code.pastDue',
    SUBSCRIPTION_CANCELED: 'user.accessBlocked.code.canceled',
    SUBSCRIPTION_SUSPENDED: 'user.accessBlocked.code.suspended',
  };

  return {
    messageId: codeToMessageId[code] || 'user.accessBlocked.description',
    message,
    code,
  };
}

/**
 * Build absolute same-origin return URLs for restricted Stripe checkout.
 * @param {'payment-setup'|'payment'} flow
 */
export function buildRestrictedBillingReturnUrls(flow) {
  const origin = window.location.origin;
  const base = flow === 'payment' ? BILLING_PAYMENT_ROUTE : BILLING_PAYMENT_SETUP_ROUTE;
  return {
    successUrl: `${origin}${base}/success`,
    cancelUrl: `${origin}${base}/cancel`,
  };
}
